import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  FileDropField,
  FileList,
  Icon,
  Paragraph,
  Spacer,
  Stack,
  Typography,
  Wizard,
  useWizardPage,
  Link,
  Title
} from '@efast_public/fjd-component-library';
import { FormatUtil } from '../../../../shared/utils/formatUtil';
import forge from 'node-forge';
import './BundId.css';
import { convertBlobToTextString } from '../../../../shared/utils/fileUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BundIdKeyPairUploadDto,
  IBundIdGetDto
} from '../../../../api/models/ZBP/BundId';
import { AxiosError } from 'axios';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { Trans, useTranslation } from 'react-i18next';
import { useNotification } from '../../../../shared/notification/useNotification';
import {
  BUNDID_ANBINDUNGSLEITFADEN_DOCS,
  FJD_DOCS_EINRICHTUNGSASSISTANT
} from '../../../../shared/utils/Links';

type AccordionLabel = 'BundID-Postfach-Zertifikat' | 'Private Key';
type DataTestId = 'uploadFieldCertificate' | 'uploadFieldPrivateKey';

type FileType = 'zbpCertificate' | 'zbpPrivateKey';

interface KeyPairFileState {
  readonly file: File | undefined;
  readonly validity: Date | undefined;
  readonly uploadDate: Date | undefined;
}

interface CertificateUploaderSectionProps {
  readonly fileType: FileType;
  readonly fileData: KeyPairFileState | undefined;
  readonly handleFileChange: (file: File, fileType: FileType) => void;
  readonly onFileDelete: (fileType: FileType) => void;
  readonly isFileUploadFieldDisabled: boolean;
}

function KeyPairUploadFields(
  props: CertificateUploaderSectionProps
): ReactElement {
  const label: AccordionLabel =
    props.fileType === 'zbpCertificate'
      ? 'BundID-Postfach-Zertifikat'
      : 'Private Key';

  const dataTestId: DataTestId =
    props.fileType === 'zbpCertificate'
      ? 'uploadFieldCertificate'
      : 'uploadFieldPrivateKey';

  return (
    <Accordion
      data-testid={`accordion-${dataTestId}`}
      before={
        <Icon
          data-testid={`statusIcon-${dataTestId}`}
          name={props.fileData ? 'checkmark-filled' : 'circle-dash'}
          variant={props.fileData ? 'success' : undefined}
          title="Status: ok"
        />
      }
      label={label}
    >
      <div style={{ paddingLeft: '2.7rem', marginBottom: '0.5rem' }}>
        <Typography variant="secondary-outline" size="s">
          {props.fileData && props.fileData.uploadDate
            ? `Hochgeladen am: ${FormatUtil.formatDateTime(props.fileData.uploadDate, 'DD.MM.YYYY')}${props.fileData.validity ? `, Gültig bis: ${FormatUtil.formatDateTime(props.fileData.validity, 'DD.MM.YYYY')}` : ', Gültig bis: unbegrenzt'}`
            : null}
        </Typography>
      </div>
      {!props.fileData ? (
        <FileDropField
          disabled={props.isFileUploadFieldDisabled}
          className="uploadField"
          data-testid={`uploadField-${dataTestId}`}
          label="Neue Datei auswählen"
          dropHelperText={'oder in dieses Feld ziehen'}
          onAdd={(files) => props.handleFileChange(files[0], props.fileType)}
        />
      ) : (
        <FileList>
          <FileList.FileItem
            data-testid={`fileItem-${dataTestId}`}
            file={{ name: props.fileData?.file?.name ?? '' }}
            onDelete={() => props.onFileDelete(props.fileType)}
          />
        </FileList>
      )}
    </Accordion>
  );
}

export default function BundId() {
  const { t } = useTranslation();
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const [zbpCertificate, setZbpCertificate] = useState<
    KeyPairFileState | undefined
  >();
  const [zbpPrivateKey, setZbpPrivateKey] = useState<
    KeyPairFileState | undefined
  >();

  const { data: zbpKeyPair } = useQuery<IBundIdGetDto, AxiosError>({
    queryKey: [BehApiRoutes.bundId()]
  });

  const { mutateAsync: updateBundIdKeyPair } = useMutation({
    mutationFn: (bundIdKeyPair: BundIdKeyPairUploadDto) =>
      notifyUserFromRequest(
        () => behClient.post(BehApiRoutes.bundId(), bundIdKeyPair),
        {
          successMessage: t('api.success.createBundIdKeyPair'),
          errorMessage: t('api.error.createBundIdKeyPair')
        }
      )
  });

  useEffect(() => {
    if (zbpKeyPair) {
      setZbpCertificate({
        file: new File([], zbpKeyPair.certificateFileName, {
          type: 'text/plain'
        }),
        uploadDate: zbpKeyPair.certificateUploadTime,
        validity: zbpKeyPair.certificateExpirationTime
      });
      setZbpPrivateKey({
        file: new File([], zbpKeyPair.privateKeyFileName, {
          type: 'text/plain'
        }),
        uploadDate: zbpKeyPair.privateKeyUploadTime,
        validity: undefined
      });
    }
  }, [zbpKeyPair]);

  const onSubmit = useCallback(async () => {
    const zbpCertificateText: string | undefined =
      await convertBlobToTextString(zbpCertificate?.file);
    const zbpPrivateKeyText: string | undefined = await convertBlobToTextString(
      zbpPrivateKey?.file
    );

    return updateBundIdKeyPair({
      certificate: zbpCertificateText,
      privateKey: zbpPrivateKeyText,
      certificateFileName: zbpCertificate?.file?.name || '',
      privateKeyFileName: zbpPrivateKey?.file?.name || ''
    });
  }, [updateBundIdKeyPair, zbpCertificate, zbpPrivateKey]);

  const handleFileChange: (
    file: File,
    fileType: FileType
  ) => Promise<void> = async (
    file: File,
    fileType: FileType
  ): Promise<void> => {
    if (!file) {
      return;
    }
    try {
      const filePEM: string = await file.text();
      if (fileType === 'zbpCertificate') {
        const cert = forge.pki.certificateFromPem(filePEM);

        const isRSA: boolean =
          cert?.publicKey && 'n' in cert.publicKey && 'e' in cert.publicKey;

        if (!isRSA) {
          notifyUser({
            intent: 'error',
            title:
              'Ungültiges Dateiformat. (Es wird nur eine Datei mit X.509 Formatierung akzeptiert.)'
          });
          return;
        }

        setZbpCertificate({
          file,
          validity: cert.validity.notAfter,
          uploadDate: new Date()
        });
      } else if (fileType === 'zbpPrivateKey') {
        try {
          forge.pki.privateKeyFromPem(filePEM);
        } catch (error) {
          notifyUser({
            intent: 'error',
            title: 'Ungültiges Dateiformat. Die Datei ist kein Private Key.'
          });
          return;
        }

        setZbpPrivateKey({
          file,
          validity: undefined,
          uploadDate: new Date()
        });
      }
    } catch (error) {
      notifyUser({
        intent: 'error',
        title:
          'Fehler beim Lesen der Datei: Bitte stellen Sie sicher, dass die Datei im PEM-Format vorliegt.'
      });
    }
  };

  useWizardPage({
    isFormValid: !!zbpCertificate && !!zbpPrivateKey,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    isStepOptional: true,
    skipButtonLabel: 'Später einrichten',
    onSubmit
  });

  const handleFileDelete: (fileType: FileType) => void = (
    fileType: FileType
  ) => {
    if (fileType === 'zbpCertificate') {
      setZbpCertificate(undefined);
    } else {
      setZbpPrivateKey(undefined);
    }
  };

  return (
    <Wizard.Page>
      <Stack spacing="m">
        <Title level={2} visualLevel={6}>
          {t('configAssistent.bundId.headline')}
        </Title>
        <Paragraph>
          <Trans
            i18nKey="configAssistent.bundId.descriptionText"
            components={{
              Link: (
                <Link href={BUNDID_ANBINDUNGSLEITFADEN_DOCS} target="_blank">
                  link
                </Link>
              )
            }}
          />
          <Trans
            i18nKey="configAssistent.configAssistentDocumentationNotice"
            components={{
              Link: (
                <Link href={FJD_DOCS_EINRICHTUNGSASSISTANT} target="_blank">
                  hier
                </Link>
              )
            }}
          />
        </Paragraph>
        <Spacer />
        <Stack spacing="s">
          {(['zbpCertificate', 'zbpPrivateKey'] as const).map((fileType) => (
            <KeyPairUploadFields
              isFileUploadFieldDisabled={false}
              key={fileType}
              fileType={fileType}
              fileData={
                fileType === 'zbpCertificate' ? zbpCertificate : zbpPrivateKey
              }
              handleFileChange={handleFileChange}
              onFileDelete={(fileType: FileType) => handleFileDelete(fileType)}
            />
          ))}
        </Stack>
        <Spacer size="3xl" />
      </Stack>
    </Wizard.Page>
  );
}
